import React, { Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { MuiThemeProvider, Typography, Divider } from '@material-ui/core';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import CASE_STUDY, { CaseStudyFormat } from '../../../constants/caseStudy.constants';
import { axuminTheme, poslumaTheme, darkGrey, disabledGrey, dividerGrey, transparentWhite } from '../../../app/theme';

const useStyles = makeStyles(() => ({
  headerImage: {
    height: 77,
    marginBottom: 20,
  },
  heading: {
    fontSize: 20,
    fontWeight: 600,
    color: darkGrey,
    marginTop: 20,
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  divider: {
    marginTop: 10,
    marginBottom: 24,
    width: '100%',
    background: dividerGrey,
  },
  list: {
    marginTop: 0,
    marginBottom: 32,
    paddingLeft: 20,
  },
  text: {
    paddingBottom: 4,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'flex-start',
    marginBottom: 3,
    '&:nth-of-type(odd)': {
      backgroundColor: transparentWhite,
    },
    '&:last-of-type': {
      marginBottom: 10,
    },
  },
  keyItem: {
    width: '40%',
    fontSize: 18,
    color: disabledGrey,
    paddingLeft: 2,
  },
  valueItem: {
    fontSize: 18,
    color: darkGrey,
    lineHeight: 1.5,
    width: '60%',
    '& > p': {
      margin: 0,
    },
  },
  superscript: {
    fontSize: 11,
  },
}));

type PresentationTabProps = {
  module: string;
  page: number;
  index?: number;
  trainingId: number;
};

const PresentationTab = ({ module, trainingId, page, index }: PresentationTabProps): JSX.Element => {
  const classes = useStyles();
  const theme = trainingId === 1 ? axuminTheme : poslumaTheme;
  const caseStudyData =
    index !== undefined
      ? (CASE_STUDY[module][page.toString()] as CaseStudyFormat[])[index] || {}
      : (CASE_STUDY[module][page.toString()] as CaseStudyFormat) || {};
  const { presentation } = caseStudyData;
  const caseStudyPath = IMAGE_PATHS.CASE_STUDY[trainingId.toString() as keyof typeof IMAGE_PATHS.CASE_STUDY];
  const link = window.location.href;
  let caseStudyCheck = '';
  if (link.includes('interpretation/poslumauptake')) {
    caseStudyCheck = '2';
  }
  if (link.includes('interpretation/poslumauptake/casestudy')) {
    caseStudyCheck = '3';
  }

  return (
    <MuiThemeProvider theme={theme}>
      <img
        className={classes.headerImage}
        src={`${BASE_URL}${caseStudyPath.CASE_STUDY_TAB_1_IMAGE.path}`}
        alt={`${caseStudyPath.CASE_STUDY_TAB_1_IMAGE.alt}`}
      />

      {presentation.map((entry) => {
        return (
          <Fragment key={entry.sectionName}>
            <Typography className={classes.heading} key={entry.sectionName}>
              {entry.sectionName}
              <sup className={classes.superscript}>{caseStudyCheck}</sup>
            </Typography>
            <Divider className={classes.divider} />
            {entry.itemsList && (
              <ul className={classes.list}>
                {entry.itemsList.map((item) => {
                  return (
                    <li key={item}>
                      <Typography variant="body1" className={classes.text}>
                        {item}
                      </Typography>
                    </li>
                  );
                })}
              </ul>
            )}
            {entry.itemMap &&
              entry.itemMap.map((item) => (
                <div className={classes.row} key={item.key}>
                  <Typography className={classes.keyItem}>{item.key}</Typography>
                  <ReactMarkdown className={classes.valueItem} rehypePlugins={[rehypeRaw]}>
                    {item.value}
                  </ReactMarkdown>
                </div>
              ))}
          </Fragment>
        );
      })}
    </MuiThemeProvider>
  );
};

export default PresentationTab;
